import { RenderFunction } from 'vue'
import {
    EcspComplianceProfile,
    EsacComplianceProfile,
    NaturalProfile,
} from '../utilities/account/useAccountDataPersonal'
import { LegalProfile } from '../utilities/account/useAccountDataLegal'
import { HideableWalletFeature } from '../utilities/useWalletSettings'
import { LegalEsacComplianceProfile } from '../components/wizard/ESAC/legal/form/forms'
import { i18n } from '../localization'

export type NaturalProfileAttribute = keyof NaturalProfile
export type LegalProfileAttribute = keyof LegalProfile

type ServerSideTranslations = {
    de: {
        account: {
            investor_account: string
            my_profile: string
            first_name: string
            last_name: string
            date_of_birth: string
            nationality: string
            address: string
            postal_code: string
            city: string
            phone_number: string
            mobile_number: string
            new_email: string
            email_address: string
            country: string
        }
    }
}

export type NaturalProfileDocumentType = 'address' | 'bank' | 'identity' | 'tax'
export type LegalProfileDocumentType = 'legal' | 'bank'

export type NaturalProfileDocumentRequirements = {
    address: boolean
    bank: boolean
    identity: boolean
}

export type CondaPepSettings = {
    required_additional_pep_information: boolean
    show_was_politically_exposed_option: boolean
}

export type LegalProfileDocumentRequirements = {
    legal: boolean
    bank: boolean
}

export type ProfileTypes = 'legal' | 'natural'

type KycProvider = 'internal' | 'external'

export type PaymentQRCodeType = 'epc' | 'swiss'

type KycSettings = {
    kyc_provider: KycProvider
}

export type ComplianceProfile = 'esac' | 'ecsp' | null

export enum EcspStatus {
    EXPERT = 'expert',
    NON_EXPERT = 'non_expert',
    EXPERT_PENDING = 'expert_pending',
    NOTHING_TO_CHECK = 'nothing_to_check',
}

export enum MembershipDocumentStatus {
    ACCEPTED = 'accepted',
    PENDING = 'pending',
    REJECTED = 'rejected',
}

export enum MembershipDocumentPostStatus {
    REQUESTED = 'requested',
    SENT = 'sent',
}

export type CondaPlatformSettings = {
    platform_name: string
    terms_url: string
    privacy_url: string
    admin_email: string
    is_iban_from_bank_account: boolean
    disable_iban_bic_validation: boolean
    payment_qr_code_type: PaymentQRCodeType
    currency: string
    compliance_profile: ComplianceProfile
    compliance_profile_required_fields: Record<keyof EsacComplianceProfile | keyof EcspComplianceProfile, string[]>
    legal_compliance_profile_required_fields: Record<
        keyof LegalEsacComplianceProfile | keyof EcspComplianceProfile,
        string[]
    >
    external_security_link: string | null
    lockable_natural_profile_attributes: Array<string>
    lockable_natural_profile_relations: Array<string>
    lockable_legal_profile_attributes: Array<string>
    lockable_legal_profile_relations: Array<string>
    locked_natural_profile_attributes: Array<string>
    voucherify_active: boolean
    self_disclosure_form_url: string | null
    certificate: boolean
    investment_desk_settings: {
        use_new_theme: boolean
        single_campaign_style: string | null
        lock_details_tab: boolean
        lock_documents_tab: boolean
        lock_investors_room_tab: boolean
        show_tos_for_unauthenticated_users: boolean
        show_minimum_investment_instead_of_minimum_funding: boolean
        hide_percentage_on_knob: boolean
        hide_funding_indicator_on_knob: boolean
        hide_funding_max_on_knob: boolean
        show_dropdown_on_hover: boolean
        hide_company_account: boolean
        hide_create_company_account: boolean
        hide_latest_investments: boolean
        wallet_currency: string | null
        show_language_select_menu_in_header: boolean
        onboarding_flow: boolean
        use_v2_frontend: boolean
        use_v2_frontend_investment_process: boolean
        use_v2_frontend_portfolio_dashboard: boolean
        use_v2_frontend_portfolio_investments: boolean
        use_external_kyc: boolean
        use_voucherify_coupon_code: boolean
        show_contract_interest_rate_investment_process: boolean
        restrict_campaign_access_to_kyc_identity_verified_users: boolean
        redirect_url_after_login: string | null
        login_only_via_external_identity_provider: string | null
        hide_cent_values: boolean
        use_swiss_currency_format: boolean
        queue_it: boolean
        queue_it_customer_id: string | null
        right_of_withdrawal: boolean
        consequences_of_revocation: boolean
        enable_cancel_order_button: boolean
    }
}

declare global {
    interface Window {
        condaRequiredNaturalProfileAttributesForInvestment: NaturalProfileAttribute[]
        condaRequiredNaturalProfileAttributes: NaturalProfileAttribute[]
        condaRequiredLegalProfileAttributesForInvestment: LegalProfileAttribute[]
        condaRequiredLegalProfileAttributes: LegalProfileAttribute[]
        condaRequiredNaturalProfileDocuments: NaturalProfileDocumentRequirements
        condaRequiredLegalProfileDocuments: LegalProfileDocumentRequirements
        condaNaturalProfileAttributeHidden: NaturalProfileAttribute[]
        condaLegalProfileAttributeHidden: LegalProfileAttribute[]
        condaVisibleNaturalProfileDocuments: NaturalProfileDocumentType[]
        condaVisibleLegalProfileDocuments: LegalProfileDocumentType[]
        condaIsCouponCodeEnabled: boolean
        condaKeycloakLoginLink: string
        condaKeycloakRegisterLink: string
        condaKeycloakUserActionUrl: string
        condaKycSettings: KycSettings
        condaShowRequired1Notice: boolean
        condaShowRequired2Notice: boolean
        condaShowEmailChangeNotice: boolean
        condaSelectableCountries: Array<string>
        condaAllCountries: SelectOption<string>[]
        condaTranslations: ServerSideTranslations
        condaHiddenNavigationSections: Array<string>
        condaHiddenLegalNavigationSections: Array<string>
        condaLockedAttributes: Array<string>

        showPostalAddressFields: boolean
        condaCurrentLanguage: string
        condaShowWalletLink: boolean
        condaShowOrdersLink: boolean
        condaWalletLink: string
        condaOrdersLink: string
        condaAccountLink: string
        condaApiEndpoint: string
        platformSettings: CondaPlatformSettings
        condaHiddenWalletFeatures: Array<HideableWalletFeature>
        createCookie: (name: string, content: string, expiration: number) => void
        condaPepSettings: CondaPepSettings
    }
}

export interface NavigationItem<A> {
    name: string
    icon?: RenderFunction
    iconActive?: RenderFunction
    href?: string
    component?: A
}

export interface NavigationItemParent<A = any> extends NavigationItem<A> {
    children?: NavigationItem<A>[]
}

export interface SelectOption<A = boolean | string | null> {
    key: A
    label: string
    disabled?: boolean
}

export interface RadioGroupSelectOption<A = string> {
    key: A
    label: string
    description: string
}

export type VerificationStatus =
    | 'not_started'
    | 'created'
    | 'pending'
    | 'in_review'
    | 'success'
    | 'failed_canceled'
    | 'failed_fraudulent'

export enum KycDocumentVerificationStatus {
    IN_PROGRESS = 'in-progress',
    ON_HOLD = 'on-hold',
    ACCEPTED = 'accepted',
    ACCEPTED_NOT_ELIGIBLE_FOR_WITHDRAWAL = 'accepted-not-eligible-for-investment', // only on client
    REJECTED = 'rejected',
    UNREADABLE = 'unreadable',
    EXPIRED = 'expired',
    WRONG_TYPE = 'wrong-type',
    WRONG_NAME = 'wrong-name',
    DUPLICATE_DOCUMENT = 'duplicate-document',
}

export enum EcspStatus {
    EXPERT = 'expert',
    EXPERT_PENDING = 'expert_pending',
    NON_EXPERT = 'non_expert',
    NOTHING_TO_CHECK = 'nothing_to_check',
}

export enum BankAccountStatus {
    VERIFICATION_PENDING = 'verification-pending',
    VERIFIED = 'verified',
    REJECTED = 'rejected',
    EXPIRED = 'expired',
}

export type KycDocumentType =
    | 'passport'
    | 'invoice'
    | 'id-card'
    | 'residence-confirmation'
    | 'bank-statement'
    | 'sdd-mandate'
    | 'company-registration'
    | 'articles-of-association'
    | 'other'

export enum KycDocumentVerificationType {
    KycId = 'kyc_id',
    KycTax = 'kyc_tax',
    KycBank = 'kyc_bank',
    KycAddress = 'kyc_address',
    KycLegal = 'kyc_legal',
    KycComplianceProfile = 'kyc_compliance_profile',
}
// gender union type
export type Gender = 'male' | 'female' | 'non-binary'

export enum NaturalProfileGender {
    MALE = 'male',
    FEMALE = 'female',
    NON_BINARY = 'non-binary',
}

export const ENUM_NATURAL_PROFILE_GENDER_MAPPING: {
    [key in NaturalProfileGender]: string
} = {
    [NaturalProfileGender.MALE]: i18n.global.t('forms.radio_male'),
    [NaturalProfileGender.FEMALE]: i18n.global.t('forms.radio_female'),
    [NaturalProfileGender.NON_BINARY]: i18n.global.t('forms.radio_non-binary'),
}

export type InputSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export enum TransactionType {
    MONEY_IN = 0,
    MONEY_OUT = 1,
    P2P = 2,
}

export enum TransactionStatus {
    SUCCESS_MONEY_IN_OUT_OR_PENDING_P2P = 0,
    SUCCESS_P2P_OR_MONEY_IN_OUT_ERROR = 3,
    PENDING = 4,
    ERROR = 6,
    CANCELLED = 7,
    WAIT_FOR_VALIDATION_FINALIZATION = 16,
}

export enum TransactionMethod {
    BANK_CARD = 0,
    INCOMING_WIRE_TRANSFER = 1,
    OUTGOING_WIRE_TRANSFER = 3,
    P2P = 4,
    IDEAL = 13,
    SEPA_DIRECT = 14,
    CHEQUE = 15,
    NEOSURF = 16,
    SOFORT = 17,
    PFS_PHYSICAL_CARD = 18,
    MULTIBANCO = 19,
    PAYSHOP = 20,
    MB_WAY = 21,
    POLISH_INSTANT_TRANSFER = 22,
    PAGARE = 23,
    MOBILE_PAY = 24,
    PAY_TRAIL = 25,
    WE_CHAT = 26,
    P24 = 27,
    MONEY_IN_BY_TPE = 28,
    TRUSTLY = 29,
}

export enum WalletStatus {
    SC_WALLET = -1,
    NOT_OPENED = 1,
    INCOMPLETE = 2,
    REJECTED = 3,
    KYC_1 = 5,
    KYC_2 = 6,
    KYC_3 = 7,
    EXPIRED = 8,
    BLOCKED = 10,
    CLOSED = 12,
    UPDATING_FROM_KYC_2_TO_3 = 13,
    ONE_TIME_CUSTOMER = 14,
    CROWDLENDING_WALLET = 15,
    TECHNIQUE_WALLET = 16,
}

export interface Voucher {
    id: string
    type: string
    gift_card_balance: number
    code: string
    expiration_date: Date
}

export enum InvestmentHorizonOption {
    SHORT_TERM = 'short_term',
    MEDIUM_TERM = 'medium_term',
    LONG_TERM = 'long_term',
    VERY_LONG_TERM = 'very_long_term',
}

export enum InvestmentKnowledgeOption {
    advanced = 'advanced',
    basic = 'basic',
    none = 'none',
}
export enum FollowMarketOption {
    regularly = 'regularly',
    occasionally = 'occasionally',
    rarely = 'rarely',
    not_at_all = 'not_at_all',
}

export enum InvestmentFrequencyOption {
    ONE_OFF = 'one_off',
    MONTHLY = 'monthly',
    QUARTERLY = 'quarterly',
    SEMI_ANNUALLY = 'semi_annually',
    ANNUALLY = 'annually',
}

export enum RiskToleranceOption {
    HIGH = 'high',
    ADVANCED = 'advanced',
    MEDIUM = 'medium',
    LOW = 'low',
}

export enum TwoFactorAuthenticationTypes {
    OTP = 'otp',
    SMS = 'sms',
    WEBAUTHN = 'webauthn',
}

export interface CouponCode {
    is_valid?: boolean
    voucher_code: string
    discount_amount: number
}

export interface ObjectWithStringKeysAndStringValue {
    [key: string]: string
}

export interface ObjectWithStringKeysAndStringArrayValue {
    [key: string]: string[]
}

export * from './orders'
